import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  CardActionArea,
  Chip,
} from '@mui/material';

import api from '../../utils/api';

// Import our component styles hook
import useComponentStyles from '../../theme/useComponentStyles';

// Import phosphor icons for consistency
import { 
  Gift, 
  ShoppingBag, 
  Heart,
  Package,
  ArrowLeft
} from '@phosphor-icons/react';

function Gifts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [weddingDetails, setWeddingDetails] = useState(null);
  const [_wedding, setWedding] = useState(null);
  const styles = useComponentStyles();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [weddingRes, detailsRes] = await Promise.all([
          api.get('/api/wedding'),
          api.get('/api/wedding-details')
        ]);
        
        setWedding(weddingRes.data);
        setWeddingDetails(detailsRes.data);
      } catch (error) {
        setError('Failed to fetch wedding information');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const _getWeddingDate = () => {
    if (!weddingDetails?.dates?.ceremonyDate) return 'Not set';
    
    try {
      return new Date(weddingDetails.dates.ceremonyDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const menuOptions = [
    {
      title: 'Wedding Favors',
      description: 'Browse and order wedding favors for your guests',
      icon: <Gift size={32} weight="regular" color="#009688" />,
      path: '/stationary-gifts/favors',
      bgColor: 'rgba(0, 150, 136, 0.1)',
      status: 'Coming soon',
    },
    {
      title: 'Gift Registry',
      description: 'Create your wedding gift registry',
      icon: <ShoppingBag size={32} weight="regular" color="#4CAF50" />,
      path: '/stationary-gifts/registry',
      bgColor: 'rgba(76, 175, 80, 0.1)',
      status: 'Coming soon',
    },
    {
      title: 'Guest Gifts',
      description: 'Special gifts for wedding party and family',
      icon: <Heart size={32} weight="regular" color="#E91E63" />,
      path: '/stationary-gifts/guest-gifts',
      bgColor: 'rgba(233, 30, 99, 0.1)',
      status: 'Coming soon',
    },
    {
      title: 'Packaging & Wrapping',
      description: 'Custom packaging and wrapping options',
      icon: <Package size={32} weight="regular" color="#9C27B0" />,
      path: '/stationary-gifts/packaging',
      bgColor: 'rgba(156, 39, 176, 0.1)',
      status: 'Coming soon',
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.containers.pageContainer}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={styles.layout.flexRow} gap={2} alignItems="center" mb={2}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={() => navigate('/stationary-gifts')}
              sx={{ minWidth: 'auto' }}
            >
              Back
            </Button>
            <Typography component="h2" className="pagename" sx={styles.text.pagename}>
              Gifts
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" paragraph sx={styles.text.sectionDescription}>
            Browse and order wedding favors, create gift registries, and manage special gifts for your guests.
          </Typography>
        </Grid>

        {/* Menu options grid */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {menuOptions.map((option, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  sx={{
                    ...styles.cards.dashboardCard,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardActionArea 
                    onClick={() => navigate(option.path)}
                    sx={{ height: '100%', p: 3 }}
                    disabled={option.status === 'Coming soon'}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        textAlign: 'center',
                        height: '100%'
                      }}>
                        <Box sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          display: 'flex',
                          lineHeight: 0,
                          '& svg': {
                            display: 'block'
                          },
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          backgroundColor: option.bgColor,
                        }}>
                          {option.icon}
                        </Box>
                        
                        <Typography variant="h6" gutterBottom sx={styles.text.cardTitle}>
                          {option.title}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {option.description}
                        </Typography>
                        
                        <Chip 
                          label={option.status}
                          size="small"
                          color={option.status === 'Ready to create' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Gifts; 